import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent } from '@agms/ui'
import { usePermissions } from '../hooks/useAuth'
import { deploymentApi, type DeploymentTask } from '../lib/api'
import { formatLabel } from '../lib/format'

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  queued: { bg: '#e5e7eb', text: '#374151', label: 'Queued' },
  in_progress: { bg: '#dbeafe', text: '#1e40af', label: 'In Progress' },
  completed: { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  failed: { bg: '#fee2e2', text: '#991b1b', label: 'Failed' },
  cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' },
  manual: { bg: '#fef3c7', text: '#92400e', label: 'Manual' },
}

const ADAPTER_LABELS: Record<string, string> = {
  processor: 'Processor (Fiserv)',
  terminal_pax: 'Terminal — PAX',
  terminal_valor: 'Terminal — Valor',
  terminal_dejavoo: 'Terminal — Dejavoo',
  terminal_unrecognized: 'Terminal — Unrecognized make',
  service_nmi: 'Gateway — NMI',
  service_agms_pay: 'AGMS Pay',
}

function PaxBuildForm({ task, onFinished }: { task: DeploymentTask; onFinished: () => Promise<void> }) {
  const [serialNumber, setSerialNumber] = useState('')
  const [terminalId, setTerminalId] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = saving || cancelling

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await deploymentApi.completePaxBuild(task.id, {
        serial_number: serialNumber,
        terminal_id: terminalId,
        notes,
      })
      await onFinished()
    } catch {
      setError('Could not mark this PAX build complete.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async () => {
    const confirmed = window.confirm(
      'Cancel this PAX task? Cancelling may move the merchant to activation and email them.',
    )
    if (!confirmed) {
      return
    }

    setCancelling(true)
    setError(null)
    try {
      await deploymentApi.cancelTask(task.merchant_id, task.id)
      await onFinished()
    } catch {
      setError('Could not cancel this PAX task.')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2" style={{ minWidth: '220px' }}>
      <p className="text-xs text-amber-800">Log the PAX build, then mark it complete.</p>
      <div>
        <label htmlFor={`pax-serial-${task.id}`} className="block text-xs font-medium text-gray-600">Serial</label>
        <input
          id={`pax-serial-${task.id}`}
          value={serialNumber}
          onChange={(event) => setSerialNumber(event.target.value)}
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <div>
        <label htmlFor={`pax-tid-${task.id}`} className="block text-xs font-medium text-gray-600">TID</label>
        <input
          id={`pax-tid-${task.id}`}
          value={terminalId}
          onChange={(event) => setTerminalId(event.target.value)}
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <div>
        <label htmlFor={`pax-notes-${task.id}`} className="block text-xs font-medium text-gray-600">Notes</label>
        <textarea
          id={`pax-notes-${task.id}`}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} isLoading={saving}>
          Mark complete
        </Button>
        <Button type="button" variant="secondary" onClick={() => void handleCancel()} disabled={busy} isLoading={cancelling}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export function DeploymentCenterPage() {
  const queryClient = useQueryClient()
  const { hasRole, canViewDeploymentTasks, canRetryDeploymentTasks } = usePermissions()
  const canLogPaxBuild = hasRole(['operations', 'management', 'super_admin'])
  const canViewQueue = canViewDeploymentTasks()
  const canRetry = canRetryDeploymentTasks()
  const [statusFilter, setStatusFilter] = useState('')
  const [retrying, setRetrying] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['deployment-tasks', statusFilter],
    queryFn: () => deploymentApi.list(statusFilter ? { status: statusFilter } : {}),
    enabled: canViewQueue,
    refetchInterval: canViewQueue ? 15_000 : false,
  })

  const handleRetry = async (taskId: string) => {
    setRetrying(taskId)
    try {
      await deploymentApi.retry(taskId)
      await queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
    } finally {
      setRetrying(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Deployment Center</h1>
        <p className="mt-1 text-sm text-gray-500">
          Equipment and gateway setup queue.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {['', 'queued', 'in_progress', 'manual', 'completed', 'failed'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: statusFilter === s ? 600 : 400,
              backgroundColor: statusFilter === s ? '#1e40af' : '#f3f4f6',
              color: statusFilter === s ? '#fff' : '#374151',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {s === '' ? 'All' : STATUS_STYLES[s]?.label ?? s}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-gray-500">Loading tasks...</p>}
      {error && <p className="text-red-600">Error loading deployment tasks</p>}

      {data && data.data.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">
              {statusFilter
                ? `No ${(STATUS_STYLES[statusFilter]?.label ?? statusFilter).toLowerCase()} tasks`
                : 'No deployment tasks yet. Tasks appear when a lead is ready for equipment setup.'}
            </p>
          </CardContent>
        </Card>
      )}

      {data && data.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg shadow-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Merchant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adapter</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map((task: DeploymentTask) => {
                const style = STATUS_STYLES[task.status] ?? STATUS_STYLES.queued
                return (
                  <tr key={task.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {task.merchant?.dba_name ?? task.application?.dba_name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {ADAPTER_LABELS[task.adapter_type] ?? formatLabel(task.adapter_type)}
                    </td>
                    <td className="px-4 py-3">
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: style.bg,
                        color: style.text,
                      }}>
                        {style.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{task.attempts}</td>
                    <td className="px-4 py-3 text-sm text-red-600" style={{ maxWidth: '250px' }}>
                      {task.last_error ? (
                        <span title={task.last_error} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {task.last_error}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {canLogPaxBuild && task.adapter_type === 'terminal_pax' && task.status === 'manual' && (
                        <PaxBuildForm
                          task={task}
                          onFinished={async () => {
                            await queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
                          }}
                        />
                      )}
                      {canRetry && task.status === 'failed' && (
                        <Button
                          variant="secondary"
                          onClick={() => handleRetry(task.id)}
                          disabled={retrying === task.id}
                          isLoading={retrying === task.id}
                        >
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
